import {
  Box,
  CalendarIcon,
  ClockIcon,
  DoctorIcon,
  LuxAccordion,
  LuxButton,
  LuxSelectablePill,
  LuxTypography,
  Stack,
  useMediaQuery,
  UserIcon,
  useTheme,
} from '@gemini/fe-ds-core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  AddressCtasWrapper,
  AddressSection,
  BookingCard,
  BookingDetailsContainer,
  CardTitleWrapper,
  ColumnWrapper,
  DetailSection,
  InsPtCtDetailsWrapper,
  ItemWrapper,
  PatientPill,
  ScheduleDetailsWrapper,
  ScheduleWrapper,
  SubSectionTitle,
} from './BookingDetails.styles';
import { useOnlineBookingStore } from '../../store/onlineBookingStore';
import { Patient, usePatientStore } from '@store/patientStore';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { useHistory } from 'react-router-dom';
import { PatientDetailsSection } from './sections/PatientDetails';
import { formatGeneralAddress, formatPhone, phoneLink } from './utils';
import { InsuranceSection } from './sections/Insurance';
import MoreInformationModal from '@components/MoreInformationModal/MoreInformationModal';
import { generateSortedPatientsList } from '@utils/functions';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import useGetTextColor from '@hooks/useGetTextColor';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import { getOnlineBookingTokenByTheme } from '@utils/designTokens';
//ICONS
import Icon_CL from '@assets/icons/icon_CL.svg';
import Icon_EEX from '@assets/icons/icon_EEX.svg';

interface DetailsProps {
  isFullRecap?: boolean;
  hideDownloadButton?: boolean;
  hideEditButton?: boolean;
  checkAppId?: boolean;
  manageOrRescheduleMode?: boolean;
  accordionModeInMobile?: boolean;
}

const BookingDetails = ({
  isFullRecap,
  hideDownloadButton = false,
  hideEditButton = false,
  manageOrRescheduleMode = false,
  checkAppId = false,
}: DetailsProps) => {
  const { t } = useTranslation();
  const { Urls } = useUrlsContext();
  const { getTextColor } = useGetTextColor();
  const history = useHistory();
  const theme = useTheme();
  // const locationData = useOnlineBookingStore(state => state.locationData);
  const setFormStep = useOnlineBookingStore(state => state.setFormStep);
  const setShowAddPatientFrame = useOnlineBookingStore(
    state => state.setShowAddPatientFrame,
  );
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const currentStep = useOnlineBookingStore(state => state.currentFormStep);
  const generalData = useOnlineBookingStore(state => state.generalInfo);
  const isRescheduling = useOnlineBookingStore(state => state.isRescheduling);
  const links = useOnlineBookingStore(state => state.links);

  const showDoctorDetails =
    useOnlineBookingStore(
      state => state.scheduleConfig?.showDoctorDetailsForWebScheduling,
    ) || false;

  const brandName = useOnlineBookingStore(state => state.brandName);

  const patients: Array<Patient | undefined> = usePatientStore(
    state => state.patients,
  );

  const contactsDetails = usePatientStore(state => state.contactsDetails);
  const currentPatient = useOnlineBookingStore(state => state.currentPatient);
  const showAddPatientFrame = useOnlineBookingStore(
    state => state.showAddPatientFrame,
  );

  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);

  const showInsuranceSection = useMemo(
    () => !!formConfigs?.askForInsuranceInformation,
    [formConfigs],
  );

  const insuranceDrivesAvailability = useMemo(
    () =>
      showInsuranceSection &&
      formConfigs?.insuranceMustDriveAvailability === true,
    [formConfigs, showInsuranceSection],
  );
  const insuranceEditUrl = insuranceDrivesAvailability
    ? Urls.SCHEDULE_EXAM
    : Urls.PATIENT_DETAILS;
  const insuranceEditStep = insuranceDrivesAvailability
    ? StepsStep.SCHEDULE_EXAM
    : StepsStep.PATIENT_DETAILS;

  const onlyTruePatients = useMemo(() => {
    if (checkAppId) {
      console.log('#onlyTruePatients.checkAppId');
      return generateSortedPatientsList(
        patients.filter(p => p?.appointmentId),
        t,
      );
    } else {
      return generateSortedPatientsList(patients, t);
    }
  }, [patients, checkAppId]);

  const isMultipatientMode = useMemo(
    () => onlyTruePatients.length > 1,
    [onlyTruePatients],
  );

  // console.log('#onlytrue patietns', onlyTruePatients);

  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);

  const getPatientName = useCallback(
    (index: number) => onlyTruePatients[index]?.content,
    [onlyTruePatients],
  );

  const isPatientSelected = useCallback(
    (key: number) => currentPatient === key,
    [currentPatient],
  );

  const onEditClick = (url: string, step: StepsStep) => {
    setFormStep(step);
    setShowAddPatientFrame(false);
    history.push(`${url}`);
  };

  const goToExternalLink = (link?: string) => {
    if (link) {
      window.location.href = link;
    } else console.log("EDIT ERROR: No link provided. Can't go anywhere!");
  };

  const checkIfLastSection = useCallback(
    (step: StepsStep) => {
      // insurance step included
      switch (step) {
        case StepsStep.APPOINTMENT_TYPE:
          return !onlyTruePatients.some(
            p =>
              (p.patient?.insurance && showInsuranceSection) ||
              p.patient?.scheduleExam ||
              p.patient?.patientDetails?.firstName,
          );
        case StepsStep.INSURANCE:
          return !onlyTruePatients.some(
            p =>
              p.patient?.scheduleExam || p.patient?.patientDetails?.firstName,
          );
        case StepsStep.SCHEDULE_EXAM:
          return !onlyTruePatients.find(
            p => p.patient?.patientDetails?.firstName,
          );
        case StepsStep.PATIENT_DETAILS:
          return !contactsDetails?.firstName || showAddPatientFrame;
        default:
          return false;
      }
    },
    [
      contactsDetails,
      currentStep,
      showAddPatientFrame,
      onlyTruePatients,
      showInsuranceSection,
    ],
  );

  function printdiv() {
    window.print();
  }

  // BEGIN breakpoint change listeners
  const [breakpoint, setBreakpoint] = useState<'mobile' | 'desktop'>(
    window.innerWidth > MobileBreakpoint ? 'desktop' : 'mobile',
  );

  useEffect(() => {
    const handleResize = () => {
      if (breakpoint === 'mobile' && window.innerWidth > MobileBreakpoint)
        setBreakpoint('desktop');
      if (breakpoint === 'desktop' && window.innerWidth <= MobileBreakpoint)
        setBreakpoint('mobile');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  // END breakpoint change listeners

  const appTypeIcon = useMemo(() => {
    const icon: Partial<Record<string, ReactNode>> = {
      CL: <Icon_CL />,
      EEX: <Icon_EEX />,
    };
    return icon;
  }, []);

  const bookingDetailsContent = useMemo(
    () => (
      <BookingCard theme={theme}>
        {generalData && currentStep >= 0 && (
          <DetailSection
            borderColor={
              theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
            }
            isLast={!onlyTruePatients.find(p => p.patient?.appointmentType)}
          >
            <SubSectionTitle>
              <LuxTypography variant="textMedium">
                {t('detailsSection.address')}
              </LuxTypography>
              {brandName && links?.storeLocator && !manageOrRescheduleMode && (
                <LuxButton
                  variant="ghost"
                  iconStart="EditIcon"
                  size="small"
                  onClick={() => goToExternalLink(links.storeLocator)}
                  style={{ textDecoration: brandName && 'underline' }}
                >
                  <LuxTypography
                    variant="textMedium"
                    fontWeight={brandName && '700!important'}
                  >
                    {t('generalCta.edit')}
                  </LuxTypography>
                </LuxButton>
              )}
            </SubSectionTitle>
            <AddressSection isFullRecap={isFullRecap}>
              <ColumnWrapper gap="0.25rem">
                <LuxTypography variant="textMdRegular">
                  {generalData.name}
                </LuxTypography>
                <LuxTypography variant="textMdRegular">
                  {formatGeneralAddress(generalData, !!brandName)}
                </LuxTypography>
                <LuxTypography variant="textMdRegular">
                  {isMobile
                    ? phoneLink(generalData.phoneNumber)
                    : generalData.phoneNumber}
                </LuxTypography>
              </ColumnWrapper>
              <AddressCtasWrapper>
                <LuxButton
                  variant="link"
                  iconStart="LocationIcon"
                  size="small"
                  aria-label={`${t('generalCta.getDirections')} (opens in a new tab)`}
                  onClick={() =>
                    window.open(
                      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(formatGeneralAddress(generalData))}`,
                      '_blank',
                    )
                  }
                >
                  <LuxTypography variant="textMdRegular">
                    {t('generalCta.getDirections')}
                  </LuxTypography>
                </LuxButton>
                <LuxButton
                  variant="link"
                  iconStart="InformationIcon"
                  size="small"
                  aria-label={`${t('generalCta.moreInfos')} (opens booking details popup)`}
                  onClick={() => setIsMoreInfoOpen(true)}
                >
                  <LuxTypography variant="textMdRegular">
                    {t('generalCta.moreInfos')}
                  </LuxTypography>
                </LuxButton>
              </AddressCtasWrapper>
            </AddressSection>
          </DetailSection>
        )}
        {onlyTruePatients.find(p => p.patient?.appointmentType) && (
          <DetailSection
            isLast={checkIfLastSection(StepsStep.APPOINTMENT_TYPE)}
            gap={'1rem'}
          >
            <SubSectionTitle>
              <LuxTypography variant="textMedium">
                {t('detailsSection.appType')}
              </LuxTypography>
              {!hideEditButton && currentStep > 0 && !isRescheduling && (
                <LuxButton
                  variant="ghost"
                  iconStart="EditIcon"
                  size="small"
                  onClick={() =>
                    onEditClick(
                      Urls.APPOINTMENT_TYPE,
                      StepsStep.APPOINTMENT_TYPE,
                    )
                  }
                  style={{ textDecoration: brandName && 'underline' }}
                >
                  <LuxTypography
                    variant="textMedium"
                    fontWeight={brandName && '700!important'}
                  >
                    {t('generalCta.edit')}
                  </LuxTypography>
                </LuxButton>
              )}
            </SubSectionTitle>
            {onlyTruePatients.map((patient, index) => {
              if (patient.patient?.appointmentType) {
                const appointmentIdTranslation =
                  patient.patient.appointmentType.idTranslation;

                return (
                  <ColumnWrapper key={index}>
                    {onlyTruePatients.length > 1 && (
                      <PatientPill
                        customTextColor={getTextColor(
                          isPatientSelected(patient.tabKey)
                            ? theme.palette.LUX.obTokens.bgColorBrandPrimary
                            : theme.palette.LUX.obTokens.bgColorBaseDefault,
                        )}
                        customColor={
                          isPatientSelected(patient.tabKey)
                            ? theme.palette.LUX.obTokens.bgColorBrandPrimary
                            : theme.palette.LUX.obTokens.bgColorBaseDefault
                        }
                        customBorderColor={
                          theme.palette.LUX.obTokens
                            .bordersColorNeutralSecondary
                        }
                      >
                        <UserIcon
                          color={
                            isPatientSelected(patient.tabKey)
                              ? 'white'
                              : 'black'
                          }
                        />
                        <LuxTypography
                          variant="textXs"
                          color={
                            isPatientSelected(patient.tabKey)
                              ? 'white'
                              : 'black'
                          }
                        >
                          {getPatientName(index)}
                        </LuxTypography>
                      </PatientPill>
                    )}
                    <LuxSelectablePill
                      selectable={false}
                      text={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{
                            background: 'transparent!important',
                          }}
                        >
                          {appTypeIcon[appointmentIdTranslation ?? ''] && (
                            <span style={{ flexShrink: 0 }}>
                              {appTypeIcon[appointmentIdTranslation ?? '']}
                            </span>
                          )}
                          <LuxTypography
                            variant="textMedium"
                            style={{ whiteSpace: 'normal' }}
                            fontWeight={400}
                          >
                            {brandName && appointmentIdTranslation
                              ? t(
                                  `appointmentTypePage.${brandName}.${appointmentIdTranslation}.title`,
                                )
                              : patient.patient.appointmentType.name}
                          </LuxTypography>
                        </Box>
                      }
                      styleWrapLuxTypography={{
                        display: 'inline-grid',
                      }}
                      customColor={
                        theme.palette.LUX.obTokens.bgColorBaseSecondary
                      }
                      customBorderColor={
                        theme.palette.LUX.obTokens.bordersColorNeutralSecondary
                      }
                    />
                  </ColumnWrapper>
                );
              } else {
                return null;
              }
            })}
          </DetailSection>
        )}
        {(showInsuranceSection || manageOrRescheduleMode) &&
          onlyTruePatients.some(p => p.patient?.insurance) && (
            <InsuranceSection
              patients={onlyTruePatients}
              currentPatient={currentPatient}
              onEditClick={onEditClick}
              hideEditButton={
                hideEditButton ||
                (!showAddPatientFrame && currentStep <= insuranceEditStep) ||
                isRescheduling
              }
              isLast={checkIfLastSection(StepsStep.INSURANCE)}
              hideInsuranceTypeLabel={!!brandName}
              editUrl={insuranceEditUrl}
              editStep={insuranceEditStep}
            />
          )}
        {onlyTruePatients.some(p => p.patient?.scheduleExam) && (
          <DetailSection
            isLast={checkIfLastSection(StepsStep.SCHEDULE_EXAM)}
            gap="1rem"
          >
            <SubSectionTitle>
              <LuxTypography variant="textMedium">
                {t('detailsSection.scheduleExam')}
              </LuxTypography>
              {!hideEditButton && currentStep > 2 && !isRescheduling && (
                <LuxButton
                  variant="ghost"
                  iconStart="EditIcon"
                  size="small"
                  onClick={() =>
                    onEditClick(Urls.SCHEDULE_EXAM, StepsStep.SCHEDULE_EXAM)
                  }
                  style={{ textDecoration: brandName && 'underline' }}
                >
                  <LuxTypography
                    variant="textMedium"
                    fontWeight={brandName && '700!important'}
                  >
                    {t('generalCta.edit')}
                  </LuxTypography>
                </LuxButton>
              )}
            </SubSectionTitle>
            {onlyTruePatients.map((patient, index) => {
              if (patient.patient?.scheduleExam) {
                return (
                  <ItemWrapper key={index} direction="vertical">
                    {onlyTruePatients.length > 1 && (
                      <PatientPill
                        customTextColor={getTextColor(
                          isPatientSelected(patient.tabKey)
                            ? theme.palette.LUX.obTokens.bgColorBrandPrimary
                            : theme.palette.LUX.obTokens.bgColorBaseDefault,
                        )}
                        customColor={
                          isPatientSelected(patient.tabKey)
                            ? theme.palette.LUX.obTokens.bgColorBrandPrimary
                            : theme.palette.LUX.obTokens.bgColorBaseDefault
                        }
                        customBorderColor={
                          theme.palette.LUX.obTokens
                            .bordersColorNeutralSecondary
                        }
                      >
                        <UserIcon
                          color={
                            isPatientSelected(patient.tabKey)
                              ? 'white'
                              : 'black'
                          }
                        />
                        <LuxTypography
                          variant="textXs"
                          color={
                            isPatientSelected(patient.tabKey)
                              ? 'white'
                              : 'black'
                          }
                        >
                          {getPatientName(index)}
                        </LuxTypography>
                      </PatientPill>
                    )}
                    <ScheduleDetailsWrapper flexRow={isMultipatientMode}>
                      <ScheduleWrapper>
                        <CalendarIcon />
                        <LuxTypography variant="textMedium" fontWeight={400}>
                          {dayjs(patient.patient.scheduleExam.day).format(
                            'MMM DD, YYYY',
                          )}
                        </LuxTypography>
                      </ScheduleWrapper>
                      <ScheduleWrapper>
                        <ClockIcon />
                        <LuxTypography variant="textMedium" fontWeight={400}>
                          {patient.patient?.scheduleExam.hour}
                        </LuxTypography>
                      </ScheduleWrapper>
                      {showDoctorDetails && (
                        <ScheduleWrapper>
                          <DoctorIcon />
                          <LuxTypography variant="textMedium" fontWeight={400}>
                            {patient.patient?.scheduleExam.doctor}
                          </LuxTypography>
                          {patient.patient?.scheduleExam.slotId
                            ?.isRemoteDoctor && (
                            <LuxSelectablePill
                              selectable={false}
                              text={t('bookingDetails.remoteDoctor')}
                              color="info"
                            />
                          )}
                        </ScheduleWrapper>
                      )}
                    </ScheduleDetailsWrapper>
                  </ItemWrapper>
                );
              } else {
                return null;
              }
            })}
          </DetailSection>
        )}
        {(onlyTruePatients.find(
          p => p.patient?.patientDetails && p.patient.patientDetails?.firstName,
        ) ||
          showAddPatientFrame) && (
          <PatientDetailsSection
            patients={onlyTruePatients}
            currentPatient={currentPatient}
            isFullRecap={isFullRecap}
            onEditClick={onEditClick}
            hideEditButton={
              hideEditButton || currentStep <= 3 || isRescheduling
            }
            isLast={checkIfLastSection(StepsStep.PATIENT_DETAILS)}
          />
        )}
        {contactsDetails?.firstName && (
          <DetailSection isLast>
            <SubSectionTitle>
              <LuxTypography variant="textMedium">
                {t('detailsSection.ctDetails')}
              </LuxTypography>
              {!hideEditButton && currentStep > 4 && !isRescheduling && (
                <LuxButton
                  variant="ghost"
                  iconStart="EditIcon"
                  size="small"
                  onClick={() =>
                    onEditClick(Urls.CONTACT_DETAILS, StepsStep.CONTACT_DETAILS)
                  }
                  style={{ textDecoration: brandName && 'underline' }}
                >
                  <LuxTypography
                    variant="textMedium"
                    fontWeight={brandName && '700!important'}
                  >
                    {t('generalCta.edit')}
                  </LuxTypography>
                </LuxButton>
              )}
            </SubSectionTitle>
            <InsPtCtDetailsWrapper halfWidth={isFullRecap}>
              <ColumnWrapper>
                <ItemWrapper>
                  <LuxTypography variant="textMdRegular">
                    {t('fieldsLabel.name')}
                  </LuxTypography>
                  <LuxTypography variant="textMedium">
                    {contactsDetails.firstName} {contactsDetails.lastName}
                  </LuxTypography>
                </ItemWrapper>
                <ItemWrapper>
                  <LuxTypography variant="textMdRegular">
                    {t('fieldsLabel.email')}
                  </LuxTypography>
                  <LuxTypography variant="textMedium">
                    {contactsDetails.email}
                  </LuxTypography>
                </ItemWrapper>
              </ColumnWrapper>
              {contactsDetails.phone?.number && (
                <ColumnWrapper>
                  <ItemWrapper>
                    <LuxTypography variant="textMdRegular">
                      {t('fieldsLabel.phone')}
                    </LuxTypography>
                    <LuxTypography variant="textMedium">
                      {formatPhone(contactsDetails.phone)}
                    </LuxTypography>
                  </ItemWrapper>
                </ColumnWrapper>
              )}
            </InsPtCtDetailsWrapper>
          </DetailSection>
        )}
      </BookingCard>
    ),
    [
      generalData,
      currentStep,
      onlyTruePatients,
      isFullRecap,
      hideEditButton,
      isRescheduling,
      showInsuranceSection,
      insuranceDrivesAvailability,
      insuranceEditStep,
      insuranceEditUrl,
      manageOrRescheduleMode,
      currentPatient,
      showAddPatientFrame,
      contactsDetails,
      showDoctorDetails,
    ],
  );

  return (
    <>
      {breakpoint === 'mobile' ? (
        <LuxAccordion
          defaultExpanded={false}
          background={'rgba(255, 255, 255, 0.5)'}
          style={{ padding: '0 1rem' }}
          summarySx={{
            margin: '0 -1rem!important',
            padding: '0 1rem!important',
            backgroundColor: 'white',
          }}
          sx={{
            '.MuiAccordionDetails-root': {
              margin: '0rem -1rem 0 -1rem!important',
              padding: '0!important',
            },
            '.MuiAccordionSummary-root': {
              borderRadius: getOnlineBookingTokenByTheme(theme, {
                keyOB: theme.palette.LUX.obTokens.deviceDeskRRadius,
                keyVX: '0.5rem',
              }),
            },
          }}
          title={
            <Stack padding="1rem 0">
              <LuxTypography variant="heading4" component="h2">
                {t('bookingDetails.title')}
              </LuxTypography>
            </Stack>
          }
        >
          {bookingDetailsContent}
        </LuxAccordion>
      ) : (
        <BookingDetailsContainer
          isFullRecap={isFullRecap}
          borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
        >
          <CardTitleWrapper
            borderColor={
              theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
            }
          >
            <LuxTypography variant="heading4" component="h2">
              {t('bookingDetails.title')}
            </LuxTypography>
            {!hideDownloadButton && isFullRecap && (
              <LuxButton
                iconStart="DownloadIcon"
                variant="ghost"
                onClick={() => printdiv()}
              >
                {t('generalCta.downloadConfirm')}
              </LuxButton>
            )}
          </CardTitleWrapper>
          {bookingDetailsContent}
        </BookingDetailsContainer>
      )}
      <MoreInformationModal
        open={isMoreInfoOpen}
        onClose={() => setIsMoreInfoOpen(false)}
        generalData={generalData}
      />
    </>
  );
};

export default BookingDetails;
